import { Box, Grid } from '@mui/material';
import { PaymentIssueMetrics, PaymentIssue, PaymentIssueDistribution } from '../../../../types/admin';
import RefreshButton from '../../system/shared/RefreshButton';
import AdminMetricCard from '../../shared/AdminMetricCard';
import PaymentIssuesTable from './PaymentIssuesTable';
import PaymentIssueDistributionChart from './PaymentIssueDistributionChart';
import { formatNumber, formatCurrency } from '../../../../utils/formatters';

interface PaymentIssuesTabProps {
  metrics: PaymentIssueMetrics;
  paymentIssues: PaymentIssue[];
  issueDistribution: PaymentIssueDistribution[];
  loading?: boolean;
  lastUpdated: string;
  onRefresh: () => void;
  onRetryPayment: (issueId: string) => Promise<{ success: boolean }>;
}

export default function PaymentIssuesTab({
  metrics,
  paymentIssues,
  issueDistribution,
  loading,
  lastUpdated,
  onRefresh,
  onRetryPayment,
}: PaymentIssuesTabProps) {
  return (
    <Box>
      {/* Header with Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <RefreshButton onRefresh={onRefresh} lastUpdated={lastUpdated} loading={loading} />
      </Box>

      {/* Payment Issue Metrics */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="Failed Payments"
            value={formatNumber(metrics.failed_payments)}
            trend={{
              value: Math.abs(metrics.failed_payments_change),
              label: `${metrics.failed_payments_change > 0 ? '+' : ''}${metrics.failed_payments_change}%`,
              direction: metrics.failed_payments_change >= 0 ? 'up' : 'down',
            }}
            subtitle="Last 30 days"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="Expired Cards"
            value={formatNumber(metrics.expired_cards)}
            trend={{
              value: Math.abs(metrics.expired_cards_change),
              label: `${metrics.expired_cards_change > 0 ? '+' : ''}${metrics.expired_cards_change}%`,
              direction: metrics.expired_cards_change >= 0 ? 'up' : 'down',
            }}
            subtitle="Requires update"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="At Risk Revenue"
            value={formatCurrency(metrics.at_risk_revenue)}
            subtitle={`${formatNumber(metrics.chargebacks)} chargebacks`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="Auto Recovery Rate"
            value={`${metrics.auto_recovery_rate}%`}
            subtitle={`${formatNumber(metrics.resolved_this_week)} resolved this week`}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Charts and Tables */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} lg={4}>
          <PaymentIssueDistributionChart data={issueDistribution} loading={loading} />
        </Grid>
        <Grid item xs={12} lg={8}>
          <PaymentIssuesTable issues={paymentIssues} loading={loading} onRetryPayment={onRetryPayment} />
        </Grid>
      </Grid>
    </Box>
  );
}
